import { useEffect, useState } from 'react';
import { CoinIcon, RoleIcon } from './Icon';
import { characterName, roleColor } from '../game/cardMeta';
import {
  fetchProfileStats,
  fetchRecentMatches,
  type MatchRecord,
  type ProfileStats,
} from '../game/profile';
import { fetchWallet } from '../game/shop';
import { useI18n } from '../i18n';
import type { Role } from '../game/types';

const ROLE_ORDER: Role[] = ['director', 'security', 'rebel', 'fixer'];

/**
 * The player's own stats card: all-time counters, per-role record, match log.
 * Rendered as the Profile tab of the combined Profile/Settings modal.
 */
export function ProfileContent({ uid, name }: { uid: string; name: string }) {
  const { t, lang } = useI18n();
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [matches, setMatches] = useState<MatchRecord[] | null>(null);
  const [coins, setCoins] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    fetchWallet(uid)
      .then((w) => alive && setCoins(w.coins))
      .catch(() => alive && setCoins(0));
    fetchProfileStats(uid)
      .then((s) => alive && setStats(s))
      .catch(
        () =>
          alive &&
          setStats({ games: 0, wins: 0, losses: 0, mvp: 0, streak: 0, bestStreak: 0 }),
      );
    fetchRecentMatches(uid)
      .then((m) => alive && setMatches(m))
      .catch(() => alive && setMatches([]));
    return () => {
      alive = false;
    };
  }, [uid]);

  const winrate = stats && stats.games > 0 ? Math.round((stats.wins / stats.games) * 100) : 0;

  // Per-role record derived from the fetched history (most recent matches).
  const byRole = new Map<Role, { games: number; wins: number }>();
  for (const m of matches ?? []) {
    if (!m.role) continue;
    const r = byRole.get(m.role) ?? { games: 0, wins: 0 };
    r.games += 1;
    if (m.won) r.wins += 1;
    byRole.set(m.role, r);
  }

  const dateLabel = (ms: number | null) =>
    ms === null
      ? ''
      : new Date(ms).toLocaleDateString(lang === 'uk' ? 'uk-UA' : 'en-US', {
          day: 'numeric',
          month: 'short',
        });

  return (
    <div className="col" style={{ gap: 16 }}>
        <div className="row between" style={{ alignItems: 'baseline' }}>
          <div className="col" style={{ gap: 4 }}>
            <div style={{ fontWeight: 800 }}>{name}</div>
            {stats !== null && stats.streak >= 2 && (
              <div className="profile-streak">
                {t.profile.streak(stats.streak)}
                {stats.bestStreak > stats.streak && (
                  <span className="mono-dim"> · {t.profile.bestStreak(stats.bestStreak)}</span>
                )}
              </div>
            )}
          </div>
          {coins !== null && (
            <div className="profile-coins" title={t.profile.coins}><CoinIcon size={15} /> {coins}</div>
          )}
        </div>

        <div className="profile-tiles">
          <StatTile value={stats ? String(stats.games) : '…'} label={t.profile.games} />
          <StatTile value={stats ? String(stats.wins) : '…'} label={t.profile.wins} />
          <StatTile value={stats ? `${winrate}%` : '…'} label={t.profile.winrate} />
          <StatTile value={stats ? `★ ${stats.mvp}` : '…'} label={t.profile.mvp} accent="var(--lime)" />
        </div>

        {byRole.size > 0 && (
          <div className="col" style={{ gap: 8 }}>
            <div className="eyebrow">{t.profile.byRole}</div>
            {ROLE_ORDER.filter((r) => byRole.has(r)).map((r) => {
              const rec = byRole.get(r)!;
              return (
                <div key={r} className="profile-role-row">
                  <span className="profile-role-name" style={{ color: roleColor[r] }}>
                    <RoleIcon role={r} size={13} /> {t.role.name[r]}
                  </span>
                  <span className="mono-dim">
                    {rec.wins}W / {rec.games - rec.wins}L
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="col" style={{ gap: 8 }}>
          <div className="eyebrow">{t.profile.recent}</div>
          {matches && matches.length === 0 && (
            <div className="mono-dim">{t.profile.empty}</div>
          )}
          {(matches ?? []).map((m) => (
            <div key={m.gameId} className="profile-match-row">
              <span className={m.won ? 'profile-result win' : 'profile-result loss'}>
                {m.won ? t.profile.won : t.profile.lost}
              </span>
              <span className="profile-match-role" style={{ color: m.role ? roleColor[m.role] : undefined }}>
                {m.role && <RoleIcon role={m.role} size={12} />} {m.role ? t.role.name[m.role] : '—'}
              </span>
              <span className="mono-dim profile-match-meta">
                {m.mvp && <span className="profile-mvp-star">★</span>}
                {characterName[m.characterId] ?? ''} · {m.playerCount} {t.profile.players}
              </span>
              <span className="mono-dim profile-match-date">{dateLabel(m.endedAtMs)}</span>
            </div>
          ))}
        </div>
    </div>
  );
}

function StatTile({ value, label, accent }: { value: string; label: string; accent?: string }) {
  return (
    <div className="profile-tile">
      <span className="profile-tile-value" style={accent ? { color: accent } : undefined}>
        {value}
      </span>
      <span className="profile-tile-label mono-dim">{label}</span>
    </div>
  );
}
